// Frontend wrapper around the shared progress helpers. Adapts the web's
// Question / Submission types into the structural ProgressQuestion /
// ProgressSubmission shapes expected by the shared module.

import {
  computeProgress as computeProgressShared,
  isQuestionComplete,
  owesFileAfter,
  matchesCondition as matchesConditionShared,
  type CompletionReason,
  type ProgressQuestion,
  type ProgressResult as SharedProgressResult,
  type ProgressSubmission,
} from '@l2u/shared/progress';
import type { Question, Submission } from './types.ts';

export type { CompletionReason };

function toProgressQuestion(q: Question): ProgressQuestion {
  return {
    id: q.id,
    type: q.type,
    required: q.required,
    requires_upload: q.requires_upload,
    requires_expiry: q.requires_expiry,
    parent_id: q.parent_id,
    condition: q.condition as { value: unknown } | null,
    visibility_rule: q.visibility_rule,
    adoptable_doc_id: q.adoptable_doc_id,
    adoption_triggers: q.adoption_triggers.map((t) => ({
      trigger_value: t.trigger_value,
      document_id: t.document_id,
    })),
    exclude_from_compliance: q.exclude_from_compliance,
    video_url: q.video_url,
    ...(q.deleted_at !== undefined && { deleted_at: q.deleted_at }),
  };
}

function toProgressSubmission(s: Submission): ProgressSubmission {
  return {
    question_id: s.question_id,
    answer: s.answer,
    submission_files: s.submission_files.map((f) => ({
      id: f.id,
      option_key: f.option_key,
      expires_at: f.expires_at,
    })),
    submission_adoptions: s.submission_adoptions.map((a) => ({
      document_id: a.document_id,
      option_key: a.option_key,
    })),
  };
}

export function matchesCondition(answer: unknown, expected: unknown): boolean {
  return matchesConditionShared(answer, expected);
}

export type ProgressBreakdown = {
  progress: number;
  totalRequired: number;
  completedRequired: number;
  visibleIds: Set<string>;
  completionByQuestion: Map<string, CompletionReason | null>;
};

export function computeProgress(
  questions: Question[],
  submissions: Submission[],
): ProgressBreakdown {
  const pq = questions.map(toProgressQuestion);
  const ps = submissions.map(toProgressSubmission);
  const result: SharedProgressResult = computeProgressShared(pq, ps);

  const subByQ = new Map<string, Submission>();
  for (const s of submissions) subByQ.set(s.question_id, s);

  const completionByQuestion = new Map<string, CompletionReason | null>();
  for (const q of questions) {
    if (!result.visibleIds.has(q.id)) continue;
    completionByQuestion.set(
      q.id,
      isQuestionComplete(toProgressQuestion(q), subByQ.get(q.id) ? toProgressSubmission(subByQ.get(q.id)!) : undefined),
    );
  }

  return {
    progress: result.progress,
    totalRequired: result.totalRequired,
    completedRequired: result.completedRequired,
    visibleIds: result.visibleIds,
    completionByQuestion,
  };
}

export function questionCompletion(
  q: Question,
  sub: Submission | undefined,
): CompletionReason | null {
  return isQuestionComplete(toProgressQuestion(q), sub ? toProgressSubmission(sub) : undefined);
}

/**
 * Would this question owe a file if every one of its live files were gone?
 * The shared owesFileAfter rule (the API's LAST_FILE_OWED gate) evaluated with
 * the answer and adoptions kept and the files emptied. Drives the reply
 * composer's `requiresFile` and the questionnaire ✕ lock on the last file.
 * Pass the effective (frozen) question, never the live row.
 */
export function questionOwesFileWithoutFiles(q: Question, sub: Submission | undefined): boolean {
  return owesFileAfter(toProgressQuestion(q), {
    question_id: q.id,
    answer: sub?.answer ?? null,
    submission_files: [],
    submission_adoptions: (sub?.submission_adoptions ?? []).map((a) => ({
      document_id: a.document_id,
      option_key: a.option_key,
    })),
  });
}

export type AdoptionTarget = {
  documentId: string;
  adoptOnly: boolean;
  /**
   * Where this target came from — the vendor UI renders the two differently.
   *
   * 'question' — the question's own adoptable_doc_id (document_adopt, or
   *   document_upload with a linked standard). These questions have NO other
   *   affordance: document_adopt gets no file input, and progress.ts still gates
   *   them on an adoption row. Their Adopt button must stay, or they become
   *   unsatisfiable.
   * 'trigger' — an adoption_triggers row. These no longer gate completion and
   *   render read-only (View standard only).
   */
  source: 'trigger' | 'question';
};

export function activeAdoptionTargets(q: Question, sub: Submission | undefined): AdoptionTarget[] {
  const targets: AdoptionTarget[] = [];
  if (
    (q.type === 'document_adopt' || q.type === 'document_upload') &&
    q.adoptable_doc_id
  ) {
    targets.push({ documentId: q.adoptable_doc_id, adoptOnly: false, source: 'question' });
  }
  for (const t of q.adoption_triggers) {
    // Unconditional (null) triggers are always offered to the vendor. Legacy
    // conditional triggers require an answer that matches their value.
    if (t.trigger_value == null) {
      targets.push({ documentId: t.document_id, adoptOnly: t.adopt_only, source: 'trigger' });
    } else if (sub?.answer != null && matchesCondition(sub.answer, t.trigger_value)) {
      targets.push({ documentId: t.document_id, adoptOnly: t.adopt_only, source: 'trigger' });
    }
  }
  return targets;
}
